import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowRight, Award, BookOpen, Eye, Rocket, Shield, Sparkles, Target, TrendingUp, Users, Zap } from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { colors } from "../../config/colors";
import { getAbout } from "../../api";

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  background: `linear-gradient(140deg, ${colors.primary[900]} 0%, ${colors.primary[700]} 50%, ${colors.secondary[600]} 100%)`,
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  "&::before": {
    content: '""',
    position: "absolute",
    width: 360,
    height: 360,
    borderRadius: "50%",
    top: -140,
    right: -80,
    background: "rgba(255,255,255,0.08)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    width: 280,
    height: 280,
    borderRadius: "50%",
    left: -100,
    bottom: -120,
    background: "rgba(255,255,255,0.06)",
  },
}));

const HeroBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  borderRadius: 999,
  padding: theme.spacing(1, 2),
  background: "rgba(255,255,255,0.16)",
  border: "1px solid rgba(255,255,255,0.24)",
  backdropFilter: "blur(6px)",
}));

const StatCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${colors.primary[100]}`,
  padding: theme.spacing(2.5),
  height: "100%",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  transition: "all 0.25s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 18px 30px rgba(15, 23, 42, 0.10)",
    borderColor: colors.primary[300],
  },
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  border: `1px solid ${colors.primary[100]}`,
  padding: theme.spacing(3),
  height: "100%",
  background: "linear-gradient(160deg, #ffffff 0%, #f9fbff 100%)",
  transition: "all 0.25s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 18px 30px rgba(15, 23, 42, 0.10)",
    borderColor: colors.primary[300],
  },
}));

const WhyCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${colors.primary[100]}`,
  padding: theme.spacing(3),
  height: "100%",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: colors.primary[300],
    transform: "translateY(-6px)",
    boxShadow: "0 18px 30px rgba(15, 23, 42, 0.10)",
  },
}));

const statIcons = [Users, BookOpen, Shield, Award];
const whyUsIcons = [TrendingUp, Shield, Users];
const defaultWhyUs = [
  {
    title: "Practical Learning Path",
    text: "Step-by-step guidance designed for real market application.",
  },
  {
    title: "Risk-First Mindset",
    text: "We prioritize discipline, risk control, and consistency over hype.",
  },
  {
    title: "Community-Led Growth",
    text: "Learners grow faster with shared insights and mentor support.",
  },
];

const AboutUs = () => {
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getAbout();
        if (!mounted) return;

        if (data && typeof data === "object") {
          setCms({
            heroTitle: data.heroTitle || "",
            heroSubtitle: data.heroSubtitle || "",
            quoteText: data.quoteText || "",
            missionTitle: data.missionTitle || "",
            missionDesc: data.missionDesc || "",
            visionTitle: data.visionTitle || "",
            visionDesc: data.visionDesc || "",
            promiseTitle: data.promiseTitle || "",
            promiseDesc: data.promiseDesc || "",
            whyUs: Array.isArray(data.whyUs) ? data.whyUs : [],
            stats: Array.isArray(data.stats) ? data.stats : [],
          });
        } else {
          setCms(null);
        }
      } catch {
        setCms(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(
    () => (cms?.stats || []).slice(0, 4).map((item, i) => ({ ...item, icon: statIcons[i] || Users })),
    [cms]
  );

  const valueCards = useMemo(
    () => [
      { title: cms?.missionTitle, desc: cms?.missionDesc, icon: Target },
      { title: cms?.visionTitle, desc: cms?.visionDesc, icon: Eye },
      { title: cms?.promiseTitle, desc: cms?.promiseDesc, icon: Zap },
    ],
    [cms]
  );

  const whyUs = useMemo(
    () => {
      const source = Array.isArray(cms?.whyUs) && cms.whyUs.length ? cms.whyUs : defaultWhyUs;
      return source.slice(0, 3).map((item, i) => ({ ...item, icon: whyUsIcons[i] || Shield }));
    },
    [cms]
  );

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (!cms) {
    return <div className="text-center py-10">No data available</div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />

      <main className="flex-grow">
        <HeroSection>
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{ color: "rgba(255,255,255,0.85)", letterSpacing: 3, fontWeight: 700 }}
              >
                About Us
              </Typography>
              <Box sx={{ mt: 2 }}>
                <HeroBadge>
                  <Sparkles size={15} color="white" />
                  <Typography sx={{ color: "white", fontWeight: 600, fontSize: "0.82rem" }}>
                    Trusted by growing market learners
                  </Typography>
                </HeroBadge>
              </Box>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  mt: 2.4,
                  color: "white",
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontSize: { xs: "2.1rem", md: "3.2rem" },
                }}
              >
                {cms.heroTitle || "Building smart traders, one step at a time"}
              </Typography>
              <Typography
                sx={{
                  mt: 2.5,
                  color: "rgba(255,255,255,0.9)",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.8,
                }}
              >
                {cms.heroSubtitle || "We help learners gain practical market confidence through structured education and tools."}
              </Typography>
              <Box sx={{ mt: 3.5, display: "flex", gap: 1.2, flexWrap: "wrap" }}>
                {["Practical Learning", "Expert Mentorship", "Real Market Focus"].map((label) => (
                  <Box
                    key={label}
                    sx={{
                      px: 1.5,
                      py: 0.7,
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.26)",
                      color: "white",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
          </Container>
        </HeroSection>

        <Container maxWidth="lg" sx={{ mt: -5, position: "relative", zIndex: 2 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              p: { xs: 3, md: 4 },
              border: `1px solid ${colors.primary[100]}`,
              background: "white",
              boxShadow: "0 20px 36px rgba(2, 6, 23, 0.08)",
            }}
          >
            <Typography sx={{ fontStyle: "italic", color: colors.gray[700], lineHeight: 1.9, fontSize: { xs: "1rem", md: "1.08rem" } }}>
              "{cms.quoteText || "Our goal is to simplify market learning and make quality guidance accessible to every learner."}"
            </Typography>
          </Paper>
        </Container>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: 2,
            }}
          >
            {stats.map((item, index) => (
              <StatCard key={`${item.label}-${index}`} elevation={0}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
                    }}
                  >
                    <item.icon size={18} color="white" />
                  </Box>
                  <Typography sx={{ color: colors.gray[600], fontSize: "0.85rem" }}>{item.label}</Typography>
                </Box>
                  <Typography sx={{ mt: 1.5, color: colors.primary[800], fontWeight: 800, fontSize: "1.6rem", lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
              </StatCard>
            ))}
          </Box>
        </Container>

        <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              color: colors.primary[900],
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
              mb: 3,
            }}
          >
            Our Core Values
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2.5,
            }}
          >
            {valueCards.map((item, i) => (
              <ValueCard key={`${item.title || "value"}-${i}`} elevation={0}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: colors.primary[50],
                    color: colors.primary[700],
                  }}
                >
                  <item.icon size={22} />
                </Box>
                <Typography sx={{ mt: 2, fontWeight: 700, color: colors.primary[800], fontSize: "1.15rem", display: "flex", alignItems: "center", gap: 1 }}>
                  {i === 0 && <Rocket size={16} />}
                  {item.title || "-"}
                </Typography>
                <Typography sx={{ mt: 1, color: colors.gray[600], lineHeight: 1.8 }}>{item.desc || "-"}</Typography>
              </ValueCard>
            ))}
          </Box>
        </Container>

        <Box
          sx={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
            py: { xs: 6, md: 8 },
            borderTop: `1px solid ${colors.gray[100]}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: 14, md: 30 },
              right: { xs: -40, md: -24 },
              width: { xs: 120, md: 180 },
              height: { xs: 120, md: 180 },
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(14,165,233,0.12))",
              filter: "blur(2px)",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: 24, md: 12 },
              left: { xs: -42, md: -20 },
              width: { xs: 110, md: 150 },
              height: { xs: 110, md: 150 },
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(59,130,246,0.14), rgba(56,189,248,0.1))",
              zIndex: 0,
            }}
          />
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                color: colors.primary[900],
                fontWeight: 800,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
                mb: 3,
              }}
            >
              Why Market Mavericks
            </Typography>
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2.5,
              }}
            >
              {whyUs.map((item, i) => (
                <WhyCard key={`${item.title || "why"}-${i}`} elevation={0}>
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.secondary[500]})`,
                    }}
                  >
                    <item.icon size={22} color="white" />
                  </Box>
                  <Typography sx={{ mt: 2, fontWeight: 700, color: colors.primary[800], fontSize: "1.05rem" }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: colors.gray[600], lineHeight: 1.75 }}>{item.text}</Typography>
                </WhyCard>
              ))}
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "22px",
              p: { xs: 3, md: 5 },
              textAlign: "center",
              background: `linear-gradient(135deg, ${colors.primary[700]}, ${colors.secondary[600]})`,
              color: "white",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Ready to start your growth journey?
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}>
              Explore our courses and build practical confidence in the markets.
            </Typography>
            <Button
              component={Link}
              to="/courses"
              variant="contained"
              endIcon={<ArrowRight size={16} />}
              sx={{
                bgcolor: "white",
                color: colors.primary[800],
                fontWeight: 700,
                px: 3.2,
                py: 1.2,
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: colors.primary[50],
                },
              }}
            >
              Explore Courses
            </Button>
          </Paper>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
